import { Button, type ButtonProps } from '@eye-able/ui';
import LoadingSpinner from '@components/LoadingSpinner';
import { cn } from '@helpers/cn';
import { useInlineTranslation } from '@/i18n/hooks/useInlineTranslation.tsx';

export interface LoadingButtonProps extends ButtonProps {
  loading: boolean;
}

/**
 * A button component that shows a loading spinner when `loading` is set to `true`. The button is disabled while loading, and the original content is
 * hidden but still rendered to maintain layout stability.
 */
const LoadingButton = ({
  'aria-label': ariaLabel,
  children,
  className,
  disabled,
  loading,
  type = 'button',
  ...rest
}: LoadingButtonProps) => {
  const { t } = useInlineTranslation();

  return (
    <Button
      type={type}
      aria-label={loading ? t('loading') : ariaLabel}
      disabled={loading || disabled}
      className={cn(
        'eye:relative eye:[&_.loader-container]:absolute eye:[&_.loader-container]:inset-0 eye:[&_.loader-container]:m-auto',
        className,
      )}
      {...rest}
    >
      {loading && <LoadingSpinner scale={2} delayMs={0} height='auto' />}
      <span
        className={cn(
          'eye:inline-flex eye:items-center eye:gap-[inherit]',
          loading && 'eye:opacity-0',
        )}
      >
        {children}
      </span>
    </Button>
  );
};

export default LoadingButton;
